import { Alert, LoadingPanel, StatusPanel } from '@dispatch/ui';
import { useCallback, useEffect, useMemo, useState, type SyntheticEvent } from 'react';
import { apiFormDataRequest, apiRequest, errorMessage } from './api.js';
import type {
  ReferenceImportPreviewRow,
  ReferenceImportRecord,
  ReferenceItem,
  ReferenceListSummary,
  ReferenceListVersion,
  TemplateSummary,
} from './types.js';

interface TemplateVersionForCopy {
  id: string;
  definition: {
    fields: {
      id: string;
      type: string;
      label: string;
      options?: { id: string; label: string; value: string }[];
    }[];
  };
}

interface ImportResponse {
  import: ReferenceImportRecord;
  preview: {
    offset: number;
    limit: number;
    rows: ReferenceImportPreviewRow[];
  };
}

export function ReferenceDataPanel({
  businessId,
  csrfToken,
  supportAccessId,
}: {
  businessId: string;
  csrfToken: string;
  supportAccessId?: string | undefined;
}) {
  const [referenceLists, setReferenceLists] = useState<ReferenceListSummary[]>([]);
  const [templates, setTemplates] = useState<TemplateSummary[]>([]);
  const [selectedListId, setSelectedListId] = useState<string>();
  const [version, setVersion] = useState<ReferenceListVersion>();
  const [items, setItems] = useState<ReferenceItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();

  const [listName, setListName] = useState('');
  const [listDescription, setListDescription] = useState('');
  const [copyMode, setCopyMode] = useState<'blank' | 'reference_list' | 'template'>('blank');
  const [copyReferenceVersionId, setCopyReferenceVersionId] = useState('');
  const [copyTemplateId, setCopyTemplateId] = useState('');
  const [copyTemplateVersion, setCopyTemplateVersion] = useState<TemplateVersionForCopy>();
  const [copyTemplateFieldId, setCopyTemplateFieldId] = useState('');

  const [itemKey, setItemKey] = useState('');
  const [itemLabel, setItemLabel] = useState('');
  const [itemValue, setItemValue] = useState('');
  const [itemPosition, setItemPosition] = useState('0');
  const [editingItemId, setEditingItemId] = useState<string>();

  const [importFile, setImportFile] = useState<File>();
  const [importFileInputKey, setImportFileInputKey] = useState(0);
  const [activeImport, setActiveImport] = useState<ReferenceImportRecord>();
  const [previewRows, setPreviewRows] = useState<ReferenceImportPreviewRow[]>([]);
  const [keyHeader, setKeyHeader] = useState('');
  const [labelHeader, setLabelHeader] = useState('');
  const [valueHeader, setValueHeader] = useState('');

  const [editingPreviewRowNumber, setEditingPreviewRowNumber] = useState<number>();
  const [previewEditKey, setPreviewEditKey] = useState('');
  const [previewEditLabel, setPreviewEditLabel] = useState('');
  const [previewEditValue, setPreviewEditValue] = useState('');

  const selectedList = useMemo(
    () => referenceLists.find((referenceList) => referenceList.id === selectedListId),
    [referenceLists, selectedListId],
  );

  const copyableReferenceVersions = useMemo(
    () =>
      referenceLists.flatMap((referenceList) => {
        const choices: { id: string; label: string }[] = [];
        if (referenceList.current_published_version_id) {
          choices.push({
            id: referenceList.current_published_version_id,
            label: `${referenceList.name} — Published v${referenceList.published_version_number ?? '?'}`,
          });
        }
        if (referenceList.current_draft_version_id) {
          choices.push({
            id: referenceList.current_draft_version_id,
            label: `${referenceList.name} — Current draft`,
          });
        }
        return choices;
      }),
    [referenceLists],
  );

  const templateCopyFields = useMemo(
    () =>
      (copyTemplateVersion?.definition.fields ?? []).filter(
        (field) =>
          ['dropdown', 'radio_buttons', 'checkboxes'].includes(field.type) &&
          (field.options?.length ?? 0) > 0,
      ),
    [copyTemplateVersion],
  );

  const loadVersion = useCallback(
    async (versionId: string | null | undefined) => {
      if (!versionId) {
        setVersion(undefined);
        setItems([]);
        return;
      }
      const result = await apiRequest<{ version: ReferenceListVersion; items: ReferenceItem[] }>(
        `/api/v1/businesses/${businessId}/reference-list-versions/${versionId}`,
        { supportAccessId },
      );
      setVersion(result.version);
      setItems(result.items);
    },
    [businessId, supportAccessId],
  );

  const loadLists = useCallback(
    async (preferredListId?: string) => {
      setLoading(true);
      setError(undefined);
      try {
        const [listResult, templateResult] = await Promise.all([
          apiRequest<{ referenceLists: ReferenceListSummary[] }>(
            `/api/v1/businesses/${businessId}/reference-lists`,
            { supportAccessId },
          ),
          apiRequest<{ templates: TemplateSummary[] }>(
            `/api/v1/businesses/${businessId}/templates`,
            { supportAccessId },
          ),
        ]);
        setReferenceLists(listResult.referenceLists);
        setTemplates(templateResult.templates);
        const nextId =
          preferredListId ??
          selectedListId ??
          listResult.referenceLists.find((referenceList) => referenceList.status === 'active')?.id;
        setSelectedListId(nextId);
        const next = listResult.referenceLists.find((referenceList) => referenceList.id === nextId);
        await loadVersion(next?.current_draft_version_id ?? next?.current_published_version_id);
      } catch (caught) {
        setError(errorMessage(caught));
      } finally {
        setLoading(false);
      }
    },
    [businessId, loadVersion, selectedListId, supportAccessId],
  );

  useEffect(() => {
    void loadLists();
  }, [businessId]);

  useEffect(() => {
    if (!selectedList) return;
    void loadVersion(
      selectedList.current_draft_version_id ?? selectedList.current_published_version_id,
    ).catch((caught: unknown) => setError(errorMessage(caught)));
    setActiveImport(undefined);
    setPreviewRows([]);
    setEditingPreviewRowNumber(undefined);
  }, [selectedList?.id]);

  useEffect(() => {
    if (!copyTemplateId) {
      setCopyTemplateVersion(undefined);
      setCopyTemplateFieldId('');
      return;
    }
    const template = templates.find((candidate) => candidate.id === copyTemplateId);
    const versionId = template?.current_published_version_id ?? template?.current_draft_version_id;
    if (!versionId) {
      setCopyTemplateVersion(undefined);
      return;
    }
    void apiRequest<{ version: TemplateVersionForCopy }>(
      `/api/v1/businesses/${businessId}/template-versions/${versionId}`,
      { supportAccessId },
    )
      .then((result) => {
        setCopyTemplateVersion(result.version);
        const field = result.version.definition.fields.find(
          (candidate) =>
            ['dropdown', 'radio_buttons', 'checkboxes'].includes(candidate.type) &&
            (candidate.options?.length ?? 0) > 0,
        );
        setCopyTemplateFieldId(field?.id ?? '');
      })
      .catch((caught: unknown) => setError(errorMessage(caught)));
  }, [businessId, copyTemplateId, supportAccessId, templates]);

  const refreshImport = useCallback(
    async (importId: string) => {
      const result = await apiRequest<ImportResponse>(
        `/api/v1/businesses/${businessId}/reference-imports/${importId}?offset=0&limit=500`,
        { supportAccessId },
      );
      setActiveImport(result.import);
      setPreviewRows(result.preview.rows);
      if (result.import.status === 'mapping_required') {
        const headers = result.import.headers;
        const mapping = result.import.mapping;
        setKeyHeader(mapping?.keyHeader ?? headers[0] ?? '');
        setLabelHeader(mapping?.labelHeader ?? headers[1] ?? headers[0] ?? '');
        setValueHeader(mapping?.valueHeader ?? headers[2] ?? headers[1] ?? headers[0] ?? '');
      }
      return result.import.status;
    },
    [businessId, supportAccessId],
  );

  useEffect(() => {
    if (!selectedList?.active_import_id) return;
    void refreshImport(selectedList.active_import_id).catch((caught: unknown) =>
      setError(errorMessage(caught)),
    );
  }, [selectedList?.active_import_id, refreshImport]);

  useEffect(() => {
    if (!activeImport || !['queued', 'processing'].includes(activeImport.status)) return;
    const timer = window.setInterval(() => {
      void refreshImport(activeImport.id).catch((caught: unknown) =>
        setError(errorMessage(caught)),
      );
    }, 1500);
    return () => window.clearInterval(timer);
  }, [activeImport?.id, activeImport?.status, refreshImport]);

  useEffect(() => {
    if (!editingItemId) setItemPosition(String(items.length));
  }, [editingItemId, items.length]);

  async function createList(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError(undefined);
    setNotice(undefined);
    try {
      let copyFrom: unknown;
      if (copyMode === 'reference_list') {
        if (!copyReferenceVersionId) throw new Error('Select the reference-list version to copy.');
        copyFrom = { type: 'reference_list', sourceVersionId: copyReferenceVersionId };
      } else if (copyMode === 'template') {
        if (!copyTemplateVersion || !copyTemplateFieldId) {
          throw new Error('Select a form and an inline choice field to copy.');
        }
        copyFrom = {
          type: 'template',
          templateVersionId: copyTemplateVersion.id,
          fieldId: copyTemplateFieldId,
        };
      }
      const result = await apiRequest<{ referenceList: ReferenceListSummary }>(
        `/api/v1/businesses/${businessId}/reference-lists`,
        {
          method: 'POST',
          csrfToken,
          supportAccessId,
          body: {
            name: listName,
            ...(listDescription.trim() ? { description: listDescription } : {}),
            ...(copyFrom ? { copyFrom } : {}),
          },
        },
      );
      setNotice(`Reference list created: ${result.referenceList.name}`);
      setListName('');
      setListDescription('');
      setCopyMode('blank');
      setCopyReferenceVersionId('');
      setCopyTemplateId('');
      setCopyTemplateVersion(undefined);
      setCopyTemplateFieldId('');
      await loadLists(result.referenceList.id);
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function createDraft() {
    if (!selectedList) return;
    setBusy(true);
    setError(undefined);
    try {
      await apiRequest(
        `/api/v1/businesses/${businessId}/reference-lists/${selectedList.id}/drafts`,
        {
          method: 'POST',
          csrfToken,
          supportAccessId,
        },
      );
      setNotice(`Draft created for ${selectedList.name}.`);
      await loadLists(selectedList.id);
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  function startEditing(item: ReferenceItem) {
    setEditingItemId(item.id);
    setItemKey(item.item_key);
    setItemLabel(item.label);
    setItemValue(item.value);
    setItemPosition(String(item.position));
  }

  function clearItemEditor() {
    setEditingItemId(undefined);
    setItemKey('');
    setItemLabel('');
    setItemValue('');
    setItemPosition(String(items.length));
  }

  async function saveItem(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    if (version?.status !== 'draft') return;
    setBusy(true);
    setError(undefined);
    try {
      const path = editingItemId
        ? `/api/v1/businesses/${businessId}/reference-list-versions/${version.id}/items/${editingItemId}`
        : `/api/v1/businesses/${businessId}/reference-list-versions/${version.id}/items`;
      await apiRequest(path, {
        method: editingItemId ? 'PATCH' : 'POST',
        csrfToken,
        supportAccessId,
        body: {
          itemKey,
          label: itemLabel,
          value: itemValue,
          position: Number.parseInt(itemPosition, 10),
        },
      });
      setNotice(editingItemId ? 'Reference item updated.' : 'Reference item added.');
      clearItemEditor();
      await loadVersion(version.id);
      await loadLists(selectedListId);
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function deleteItem(item: ReferenceItem) {
    if (version?.status !== 'draft') return;
    setBusy(true);
    setError(undefined);
    try {
      await apiRequest(
        `/api/v1/businesses/${businessId}/reference-list-versions/${version.id}/items/${item.id}`,
        { method: 'DELETE', csrfToken, supportAccessId },
      );
      if (editingItemId === item.id) clearItemEditor();
      setNotice(`Reference item deleted: ${item.label}`);
      await loadVersion(version.id);
      await loadLists(selectedListId);
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function publishDraft() {
    if (version?.status !== 'draft') return;
    setBusy(true);
    setError(undefined);
    try {
      const result = await apiRequest<{
        version: ReferenceListVersion;
        impact: { affectedJobCount: number; affectedJobWorkflowAvailable: boolean; reason: string };
      }>(`/api/v1/businesses/${businessId}/reference-list-versions/${version.id}/publish`, {
        method: 'POST',
        csrfToken,
        supportAccessId,
      });
      setNotice(`Published reference-data version ${result.version.version_number ?? ''}.`);
      await loadLists(selectedListId);
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function uploadImport(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedList || !importFile) return;
    setBusy(true);
    setError(undefined);
    setNotice(undefined);
    try {
      const formData = new FormData();
      formData.append('file', importFile, importFile.name);
      const result = await apiFormDataRequest<{ import: ReferenceImportRecord }>(
        `/api/v1/businesses/${businessId}/reference-lists/${selectedList.id}/imports`,
        formData,
        { csrfToken, supportAccessId },
      );
      setActiveImport(result.import);
      setPreviewRows([]);
      setNotice(`Import queued: ${importFile.name}`);
      await refreshImport(result.import.id);
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function saveMapping(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!activeImport) return;
    setBusy(true);
    setError(undefined);
    try {
      const result = await apiRequest<{ import: ReferenceImportRecord }>(
        `/api/v1/businesses/${businessId}/reference-imports/${activeImport.id}/mapping`,
        {
          method: 'PUT',
          csrfToken,
          supportAccessId,
          body: { mapping: { keyHeader, labelHeader, valueHeader } },
        },
      );
      setActiveImport(result.import);
      setPreviewRows([]);
      setNotice('Column mapping saved. Import preview queued.');
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  function startEditingPreviewRow(row: ReferenceImportPreviewRow) {
    setEditingPreviewRowNumber(row.row_number);
    setPreviewEditKey(row.item_key ?? '');
    setPreviewEditLabel(row.label ?? '');
    setPreviewEditValue(row.value ?? '');
  }

  function cancelPreviewRowEdit() {
    setEditingPreviewRowNumber(undefined);
    setPreviewEditKey('');
    setPreviewEditLabel('');
    setPreviewEditValue('');
  }

  async function saveImportPreviewRow() {
    if (!activeImport || editingPreviewRowNumber === undefined) return;
    setBusy(true);
    setError(undefined);
    try {
      await apiRequest(
        `/api/v1/businesses/${businessId}/reference-imports/${activeImport.id}/rows/${editingPreviewRowNumber}`,
        {
          method: 'PATCH',
          csrfToken,
          supportAccessId,
          body: {
            itemKey: previewEditKey,
            label: previewEditLabel,
            value: previewEditValue,
          },
        },
      );
      setNotice(`Import row ${editingPreviewRowNumber} updated and revalidated.`);
      cancelPreviewRowEdit();
      await refreshImport(activeImport.id);
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function deleteImportPreviewRow(row: ReferenceImportPreviewRow) {
    if (!activeImport) return;
    setBusy(true);
    setError(undefined);
    try {
      await apiRequest(
        `/api/v1/businesses/${businessId}/reference-imports/${activeImport.id}/rows/${row.row_number}`,
        { method: 'DELETE', csrfToken, supportAccessId },
      );
      if (editingPreviewRowNumber === row.row_number) cancelPreviewRowEdit();
      setNotice(`Import row ${row.row_number} removed and preview revalidated.`);
      await refreshImport(activeImport.id);
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function applyImport() {
    if (!activeImport) return;
    setBusy(true);
    setError(undefined);
    try {
      const result = await apiRequest<{
        import: ReferenceImportRecord;
        version: ReferenceListVersion;
      }>(`/api/v1/businesses/${businessId}/reference-imports/${activeImport.id}/apply`, {
        method: 'POST',
        csrfToken,
        supportAccessId,
      });
      setActiveImport(undefined);
      setPreviewRows([]);
      setImportFile(undefined);
      setImportFileInputKey((current) => current + 1);
      cancelPreviewRowEdit();
      setNotice('Import applied to the current draft. It has not been published.');
      await loadVersion(result.version.id);
      await loadLists(selectedListId);
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function discardImport() {
    if (!activeImport) return;
    setBusy(true);
    setError(undefined);
    try {
      await apiRequest(`/api/v1/businesses/${businessId}/reference-imports/${activeImport.id}`, {
        method: 'DELETE',
        csrfToken,
        supportAccessId,
      });
      setNotice(`Import discarded: ${activeImport.source_filename}`);
      setActiveImport(undefined);
      setPreviewRows([]);
      setImportFile(undefined);
      setImportFileInputKey((current) => current + 1);
      cancelPreviewRowEdit();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <LoadingPanel message="Loading reference data…" />;

  return (
    <section className="panel" aria-labelledby="reference-data-heading">
      <h2 id="reference-data-heading">Reference data</h2>
      <p>
        Manage reusable choice lists. Published versions are immutable; changes are made in a draft
        and published as a new version. CSV and Excel .xlsx imports always produce a preview before
        changing the draft.
      </p>

      {error ? <Alert title="Reference-data request failed">{error}</Alert> : null}
      {notice ? <Alert tone="success">{notice}</Alert> : null}

      <StatusPanel heading="Reference-data records" tone="neutral">
        <p>
          {referenceLists.length} reference list{referenceLists.length === 1 ? '' : 's'} in this
          business.
        </p>
      </StatusPanel>

      <div className="reference-data-layout">
        <section className="card reference-list-card" aria-labelledby="reference-list-heading">
          <h3 id="reference-list-heading">Reference lists</h3>
          <div className="reference-list-grid">
            {referenceLists.map((referenceList) => (
              <button
                key={referenceList.id}
                type="button"
                className="secondary reference-list-button"
                aria-pressed={selectedListId === referenceList.id}
                onClick={() => setSelectedListId(referenceList.id)}
              >
                <span>{referenceList.name}</span>
                <small>
                  {referenceList.current_draft_version_id
                    ? `${referenceList.draft_item_count} draft items`
                    : referenceList.current_published_version_id
                      ? `Published v${referenceList.published_version_number ?? '?'} · ${referenceList.published_item_count} items`
                      : 'No version'}
                </small>
              </button>
            ))}
          </div>

          <form
            className="form-grid reference-create-form"
            onSubmit={(event) => void createList(event)}
          >
            <h4>Create reference list</h4>
            <div className="form-field">
              <label htmlFor="reference-list-name">Name</label>
              <input
                id="reference-list-name"
                required
                value={listName}
                onChange={(event) => setListName(event.target.value)}
              />
            </div>
            <div className="form-field">
              <label htmlFor="reference-list-description">Description</label>
              <input
                id="reference-list-description"
                value={listDescription}
                onChange={(event) => setListDescription(event.target.value)}
              />
            </div>
            <div className="form-field">
              <label htmlFor="reference-copy-mode">Starting data</label>
              <select
                id="reference-copy-mode"
                value={copyMode}
                onChange={(event) => setCopyMode(event.target.value as typeof copyMode)}
              >
                <option value="blank">Blank list</option>
                <option value="reference_list">Copy another reference list</option>
                <option value="template">Copy choices from a form</option>
              </select>
            </div>
            {copyMode === 'reference_list' ? (
              <div className="form-field">
                <label htmlFor="reference-copy-list">Source version</label>
                <select
                  id="reference-copy-list"
                  required
                  value={copyReferenceVersionId}
                  onChange={(event) => setCopyReferenceVersionId(event.target.value)}
                >
                  <option value="">Select a source</option>
                  {copyableReferenceVersions.map((choice) => (
                    <option key={choice.id} value={choice.id}>
                      {choice.label}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}
            {copyMode === 'template' ? (
              <>
                <div className="form-field">
                  <label htmlFor="reference-copy-template">Source form</label>
                  <select
                    id="reference-copy-template"
                    required
                    value={copyTemplateId}
                    onChange={(event) => setCopyTemplateId(event.target.value)}
                  >
                    <option value="">Select a form</option>
                    {templates.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-field">
                  <label htmlFor="reference-copy-template-field">Choice field</label>
                  <select
                    id="reference-copy-template-field"
                    required
                    value={copyTemplateFieldId}
                    onChange={(event) => setCopyTemplateFieldId(event.target.value)}
                  >
                    <option value="">Select a choice field</option>
                    {templateCopyFields.map((field) => (
                      <option key={field.id} value={field.id}>
                        {field.label} ({field.options?.length ?? 0} choices)
                      </option>
                    ))}
                  </select>
                </div>
              </>
            ) : null}
            <button type="submit" disabled={busy || listName.trim().length === 0}>
              Create reference list
            </button>
          </form>
        </section>

        <section
          className="card reference-version-card"
          aria-labelledby="reference-version-heading"
        >
          <h3 id="reference-version-heading">{selectedList?.name ?? 'Select a reference list'}</h3>
          {!selectedList ? (
            <Alert tone="info">Create or select a reference list.</Alert>
          ) : (
            <>
              <p>
                {selectedList.description ?? 'No description.'}
                <br />
                {version?.status === 'draft'
                  ? `Editing draft · ${items.length} items`
                  : version?.status === 'published'
                    ? `Published version ${version.version_number ?? ''} · ${items.length} items`
                    : 'No version available.'}
              </p>

              {!selectedList.current_draft_version_id &&
              selectedList.current_published_version_id ? (
                <button type="button" onClick={() => void createDraft()} disabled={busy}>
                  Create new draft
                </button>
              ) : null}

              {version?.status === 'draft' ? (
                <>
                  <form
                    className="form-grid reference-item-editor"
                    onSubmit={(event) => void saveItem(event)}
                  >
                    <h4>{editingItemId ? 'Edit item' : 'Add item'}</h4>
                    <div className="reference-item-fields">
                      <div className="form-field">
                        <label htmlFor="reference-item-key">Key</label>
                        <input
                          id="reference-item-key"
                          required
                          value={itemKey}
                          onChange={(event) => setItemKey(event.target.value)}
                        />
                      </div>
                      <div className="form-field">
                        <label htmlFor="reference-item-label">Label</label>
                        <input
                          id="reference-item-label"
                          required
                          value={itemLabel}
                          onChange={(event) => setItemLabel(event.target.value)}
                        />
                      </div>
                      <div className="form-field">
                        <label htmlFor="reference-item-value">Value</label>
                        <input
                          id="reference-item-value"
                          required
                          value={itemValue}
                          onChange={(event) => setItemValue(event.target.value)}
                        />
                      </div>
                      <div className="form-field">
                        <label htmlFor="reference-item-position">Order</label>
                        <input
                          id="reference-item-position"
                          type="number"
                          min="0"
                          max="999999"
                          required
                          value={itemPosition}
                          onChange={(event) => setItemPosition(event.target.value)}
                        />
                      </div>
                    </div>
                    <div className="actions">
                      <button
                        type="submit"
                        disabled={
                          busy ||
                          itemKey.trim().length === 0 ||
                          itemLabel.trim().length === 0 ||
                          itemValue.trim().length === 0 ||
                          !/^\d+$/u.test(itemPosition)
                        }
                      >
                        {editingItemId ? 'Save item' : 'Add item'}
                      </button>
                      {editingItemId ? (
                        <button type="button" className="secondary" onClick={clearItemEditor}>
                          Cancel edit
                        </button>
                      ) : null}
                    </div>
                  </form>

                  <form
                    className="reference-import-form"
                    onSubmit={(event) => void uploadImport(event)}
                  >
                    <h4>Import CSV or Excel</h4>
                    <div className="form-field">
                      <label htmlFor="reference-import-file">Import file</label>
                      <input
                        key={importFileInputKey}
                        id="reference-import-file"
                        type="file"
                        accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                        required
                        onChange={(event) => setImportFile(event.target.files?.[0])}
                      />
                    </div>
                    <button type="submit" disabled={busy || !importFile}>
                      Upload and preview
                    </button>
                  </form>
                </>
              ) : null}
            </>
          )}
        </section>
      </div>

      {selectedList && version ? (
        <section aria-labelledby="reference-items-heading">
          <div className="reference-section-heading">
            <div>
              <h3 id="reference-items-heading">Items</h3>
              <p>
                {items.length} item{items.length === 1 ? '' : 's'} in this version.
              </p>
            </div>
            {version.status === 'draft' ? (
              <button type="button" onClick={() => void publishDraft()} disabled={busy}>
                Publish draft
              </button>
            ) : null}
          </div>
          {items.length === 0 ? (
            <Alert tone="info">This version has no reference items yet.</Alert>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Key</th>
                    <th>Label</th>
                    <th>Value</th>
                    <th>Order</th>
                    {version.status === 'draft' ? <th>Actions</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.id}>
                      <td>{item.item_key}</td>
                      <td>{item.label}</td>
                      <td>{item.value}</td>
                      <td>{item.position}</td>
                      {version.status === 'draft' ? (
                        <td>
                          <div className="actions">
                            <button
                              type="button"
                              className="secondary"
                              onClick={() => startEditing(item)}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              className="danger"
                              onClick={() => void deleteItem(item)}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      ) : null}

      {activeImport ? (
        <section
          className="card reference-import-preview"
          aria-labelledby="reference-import-heading"
        >
          <h3 id="reference-import-heading">Import preview — {activeImport.source_filename}</h3>
          <p>
            Status: <strong>{activeImport.status.replaceAll('_', ' ')}</strong> · Processed{' '}
            {activeImport.processed_rows} of {activeImport.total_rows} rows.
          </p>
          {activeImport.error_message ? <Alert>{activeImport.error_message}</Alert> : null}

          {['mapping_required', 'preview_ready', 'failed'].includes(activeImport.status) ? (
            <div className="actions">
              <button
                type="button"
                className="secondary"
                onClick={() => void discardImport()}
                disabled={busy}
              >
                Discard / choose another file
              </button>
            </div>
          ) : null}

          {activeImport.status === 'mapping_required' ? (
            <form className="reference-mapping-form" onSubmit={(event) => void saveMapping(event)}>
              <h4>Map columns</h4>
              <p>
                Select which file columns supply the stable key, display label and stored value.
              </p>
              <div className="reference-item-fields">
                <div className="form-field">
                  <label htmlFor="reference-map-key">Key column</label>
                  <select
                    id="reference-map-key"
                    value={keyHeader}
                    onChange={(event) => setKeyHeader(event.target.value)}
                  >
                    {activeImport.headers.map((header) => (
                      <option key={header}>{header}</option>
                    ))}
                  </select>
                </div>
                <div className="form-field">
                  <label htmlFor="reference-map-label">Label column</label>
                  <select
                    id="reference-map-label"
                    value={labelHeader}
                    onChange={(event) => setLabelHeader(event.target.value)}
                  >
                    {activeImport.headers.map((header) => (
                      <option key={header}>{header}</option>
                    ))}
                  </select>
                </div>
                <div className="form-field">
                  <label htmlFor="reference-map-value">Value column</label>
                  <select
                    id="reference-map-value"
                    value={valueHeader}
                    onChange={(event) => setValueHeader(event.target.value)}
                  >
                    {activeImport.headers.map((header) => (
                      <option key={header}>{header}</option>
                    ))}
                  </select>
                </div>
              </div>
              <button type="submit" disabled={busy || !keyHeader || !labelHeader || !valueHeader}>
                Save mapping and build preview
              </button>
            </form>
          ) : null}

          {activeImport.status === 'preview_ready' ? (
            <>
              <div className="reference-import-counts" aria-label="Import classification counts">
                <span>New: {activeImport.new_count}</span>
                <span>Changed: {activeImport.changed_count}</span>
                <span>Unchanged: {activeImport.unchanged_count}</span>
                <span>Duplicate: {activeImport.duplicate_count}</span>
                <span>Invalid: {activeImport.invalid_count}</span>
              </div>
              {activeImport.duplicate_count > 0 || activeImport.invalid_count > 0 ? (
                <Alert tone="warning">
                  Resolve duplicate or invalid rows below using Edit or Delete. The preview is
                  revalidated after each change, and Apply unlocks automatically when no blocking
                  rows remain.
                </Alert>
              ) : null}
              <p>
                Edits and deletions affect this staged import only. The uploaded source file is
                retained unchanged for traceability.
              </p>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Row</th>
                      <th>Result</th>
                      <th>Key</th>
                      <th>Label</th>
                      <th>Value</th>
                      <th>Errors</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.map((row) => {
                      const isEditing = editingPreviewRowNumber === row.row_number;
                      return (
                        <tr key={row.row_number}>
                          <td>{row.row_number}</td>
                          <td>{row.classification}</td>
                          <td>
                            {isEditing ? (
                              <input
                                aria-label={`Import row ${row.row_number} key`}
                                value={previewEditKey}
                                onChange={(event) => setPreviewEditKey(event.target.value)}
                              />
                            ) : (
                              (row.item_key ?? '')
                            )}
                          </td>
                          <td>
                            {isEditing ? (
                              <input
                                aria-label={`Import row ${row.row_number} label`}
                                value={previewEditLabel}
                                onChange={(event) => setPreviewEditLabel(event.target.value)}
                              />
                            ) : (
                              (row.label ?? '')
                            )}
                          </td>
                          <td>
                            {isEditing ? (
                              <input
                                aria-label={`Import row ${row.row_number} value`}
                                value={previewEditValue}
                                onChange={(event) => setPreviewEditValue(event.target.value)}
                              />
                            ) : (
                              (row.value ?? '')
                            )}
                          </td>
                          <td>{row.errors.join(' ')}</td>
                          <td>
                            <div className="actions">
                              {isEditing ? (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => void saveImportPreviewRow()}
                                    disabled={
                                      busy ||
                                      previewEditKey.trim().length === 0 ||
                                      previewEditLabel.trim().length === 0 ||
                                      previewEditValue.trim().length === 0
                                    }
                                  >
                                    Save
                                  </button>
                                  <button
                                    type="button"
                                    className="secondary"
                                    onClick={cancelPreviewRowEdit}
                                    disabled={busy}
                                  >
                                    Cancel
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    type="button"
                                    className="secondary"
                                    onClick={() => startEditingPreviewRow(row)}
                                    disabled={busy}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    type="button"
                                    className="danger"
                                    onClick={() => void deleteImportPreviewRow(row)}
                                    disabled={busy}
                                  >
                                    Delete
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {activeImport.total_rows > previewRows.length ? (
                <p>
                  Showing the first {previewRows.length} of {activeImport.total_rows} preview rows.
                </p>
              ) : null}
              <button
                type="button"
                onClick={() => void applyImport()}
                disabled={
                  busy || activeImport.duplicate_count > 0 || activeImport.invalid_count > 0
                }
              >
                Apply preview to draft
              </button>
            </>
          ) : null}
        </section>
      ) : null}
    </section>
  );
}
